import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, X, Compass } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { title: "", description: "", item_ids: [], order: 0, published: true };

export const AdminReadingPaths = () => {
  const [paths, setPaths] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [rp, ri] = await Promise.all([
        axios.get(`${API}/admin/reading-paths`, { withCredentials: true }),
        axios.get(`${API}/admin/library`, { withCredentials: true }),
      ]);
      setPaths(rp.data.paths || []);
      setAllItems(ri.data.items || []);
    } catch { toast.error("Erro a carregar percursos."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const itemById = (id) => allItems.find((i) => i.id === id);

  const save = async (e) => {
    e?.preventDefault?.();
    const payload = { ...editing, order: Number(editing.order) || 0 };
    try {
      if (editing.id) {
        await axios.patch(`${API}/admin/reading-paths/${editing.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/reading-paths`, payload, { withCredentials: true });
      }
      toast.success("Percurso guardado.");
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro a guardar."); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Apagar percurso "${p.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/reading-paths/${p.id}`, { withCredentials: true });
      toast.success("Apagado.");
      load();
    } catch { toast.error("Erro."); }
  };

  const togglePublished = async (p) => {
    try {
      await axios.patch(`${API}/admin/reading-paths/${p.id}`, { published: !p.published }, { withCredentials: true });
      load();
    } catch { toast.error("Erro."); }
  };

  const moveItem = (idx, dir) => {
    const ids = [...editing.item_ids];
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    setEditing({ ...editing, item_ids: ids });
  };

  const addItem = (id) => {
    if (!id || editing.item_ids.includes(id)) return;
    setEditing({ ...editing, item_ids: [...editing.item_ids, id] });
  };

  return (
    <div className="mt-8" data-testid="admin-reading-paths">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-xl">
          Sequências curadas de recursos da biblioteca, ordenadas segundo o método: Vida → Evangelho → Igreja → Kentenich → decisão → missão.
        </p>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] hover:bg-[#093630] transition-colors shrink-0"
          data-testid="path-create-button"
        >
          <Plus className="h-4 w-4" /> Novo percurso
        </button>
      </div>

      {editing && (
        <form onSubmit={save} className="mt-6 bg-white border border-[hsl(var(--border))] p-6 sm:p-8 space-y-5" data-testid="path-edit-form">
          <h2 className="display-heading text-2xl">{editing.id ? "Editar percurso" : "Novo percurso"}</h2>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">Título *</label>
            <input
              type="text" required value={editing.title}
              onChange={(e) => setEditing({ ...editing, title: e.target.value })}
              className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
              data-testid="path-title"
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">Descrição</label>
            <textarea
              rows={2} value={editing.description || ""}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
              data-testid="path-description"
            />
          </div>

          {/* Ordered items */}
          <div>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">Recursos do percurso (por ordem)</label>
            {editing.item_ids.length === 0 ? (
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Ainda sem recursos. Adicione abaixo.</p>
            ) : (
              <ul className="space-y-1.5" data-testid="path-items-list">
                {editing.item_ids.map((id, idx) => {
                  const it = itemById(id);
                  return (
                    <li key={id} className="flex items-center gap-2 bg-[hsl(var(--secondary))]/50 border border-[hsl(var(--border))] px-3 py-2 text-sm">
                      <span className="display-heading text-base text-[hsl(var(--accent))] w-7">{String(idx + 1).padStart(2, "0")}</span>
                      <span className="flex-1 truncate">{it ? it.title : `(item removido: ${id.slice(0, 8)})`}</span>
                      <button type="button" onClick={() => moveItem(idx, -1)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid={`path-item-up-${idx}`}><ArrowUp className="h-4 w-4" /></button>
                      <button type="button" onClick={() => moveItem(idx, 1)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid={`path-item-down-${idx}`}><ArrowDown className="h-4 w-4" /></button>
                      <button type="button" onClick={() => setEditing({ ...editing, item_ids: editing.item_ids.filter((x) => x !== id) })} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]" data-testid={`path-item-remove-${idx}`}><X className="h-4 w-4" /></button>
                    </li>
                  );
                })}
              </ul>
            )}
            <select
              value=""
              onChange={(e) => addItem(e.target.value)}
              className="mt-3 w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
              data-testid="path-add-item-select"
            >
              <option value="">+ Adicionar recurso…</option>
              {allItems
                .filter((i) => !editing.item_ids.includes(i.id))
                .map((i) => (
                  <option key={i.id} value={i.id}>
                    [{i.kind === "video" ? "Vídeo" : i.kind === "itinerary" ? "Texto" : "Doc"}] {i.title}{i.published ? "" : " (rascunho)"}
                  </option>
                ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mb-1.5">Ordem</label>
              <input
                type="number" value={editing.order ?? 0}
                onChange={(e) => setEditing({ ...editing, order: e.target.value })}
                className="w-full bg-white border border-[hsl(var(--border))] px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
                data-testid="path-order"
              />
            </div>
            <label className="flex items-end gap-2 cursor-pointer pb-3">
              <input
                type="checkbox" checked={!!editing.published}
                onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                className="h-4 w-4 accent-[hsl(var(--primary))]"
                data-testid="path-published"
              />
              <span className="text-sm">Publicar</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-[hsl(var(--border))]">
            <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 text-xs uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="path-cancel">Cancelar</button>
            <button type="submit" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[hsl(var(--primary))] text-white text-xs uppercase tracking-[0.18em] hover:bg-[#093630]" data-testid="path-save">Guardar</button>
          </div>
        </form>
      )}

      <div className="mt-6">
        {loading ? (
          <p className="text-[hsl(var(--muted-foreground))]">A carregar…</p>
        ) : paths.length === 0 ? (
          <div className="bg-white border border-[hsl(var(--border))] p-12 text-center">
            <Compass className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto" />
            <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">Ainda sem percursos de leitura.</p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="paths-list">
            {paths.map((p) => (
              <li key={p.id} className={`bg-white border border-[hsl(var(--border))] p-4 flex items-start justify-between gap-3 ${!p.published ? "opacity-60" : ""}`} data-testid={`path-row-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium truncate">{p.title}</h3>
                    {!p.published && <span className="text-[10px] uppercase tracking-wider bg-[hsl(var(--muted))] px-1.5 py-0.5">Rascunho</span>}
                  </div>
                  <div className="mt-1 text-[11px] text-[hsl(var(--muted-foreground))]">
                    {(p.item_ids || []).length} recurso{(p.item_ids || []).length === 1 ? "" : "s"} · Ordem: {p.order}
                  </div>
                </div>
                <div className="inline-flex items-center gap-2 shrink-0">
                  <button onClick={() => togglePublished(p)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid={`path-toggle-${p.id}`}>
                    {p.published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button onClick={() => setEditing({ ...p })} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid={`path-edit-${p.id}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(p)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]" data-testid={`path-delete-${p.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
